import { CANONICAL_PERSONAS, MODULE_KEYS } from './canonical-personas.js';
import { PERSONA_POLICY_REGISTRY } from './persona-policy.registry.js';
import { PERMISSION_BUNDLE_REGISTRY } from './permission-bundle.registry.js';
import { canonicalRouteRegistry } from '../experience/routes/routes.js';
import { canonicalPageRegistry } from '../experience/pages/page-registry.js';
import { CANONICAL_OPERATIONS_REGISTRY } from '../operations/index.js';
function assert(condition, message) {
    if (!condition) {
        throw new Error(`PERSONA_POLICY_VALIDATION_ERROR: ${message}`);
    }
}
export function validatePersonaPolicyRegistry() {
    const configured = Object.keys(PERSONA_POLICY_REGISTRY);
    for (const persona of CANONICAL_PERSONAS) {
        assert(Boolean(PERSONA_POLICY_REGISTRY[persona]), `Missing persona policy for ${persona}`);
    }
    for (const persona of configured) {
        assert(CANONICAL_PERSONAS.includes(persona), `Unknown persona configuration ${persona}`);
    }
    const knownRoutes = new Set(canonicalRouteRegistry.map((route) => route.routeId));
    const knownPages = new Set(canonicalPageRegistry.map((page) => page.id));
    const routeById = new Map(canonicalRouteRegistry.map((route) => [route.routeId, route]));
    for (const policy of Object.values(PERSONA_POLICY_REGISTRY)) {
        assert(knownRoutes.has(policy.defaultLandingRouteId), `Unknown landing route ${policy.defaultLandingRouteId}`);
        for (const routeId of policy.navigation.candidatePrimaryRouteIds) {
            assert(knownRoutes.has(routeId), `Unknown candidate primary route ${routeId} for ${policy.persona}`);
            const route = routeById.get(routeId);
            assert(Boolean(route), `Missing descriptor for route ${routeId}`);
            assert(Boolean(route.pageId) && knownPages.has(route.pageId), `Candidate route ${routeId} must reference an implemented page`);
            if (route.operationId) {
                assert(Boolean(CANONICAL_OPERATIONS_REGISTRY[route.operationId]), `Candidate route ${routeId} references unknown operation ${route.operationId}`);
            }
            assert(Boolean(route.module), `Candidate route ${routeId} must declare module`);
            if (route.parentRouteId) {
                assert(knownRoutes.has(route.parentRouteId), `Candidate route ${routeId} has unknown parent route ${route.parentRouteId}`);
            }
        }
        for (const routeId of policy.navigation.avatarRouteIds) {
            assert(knownRoutes.has(routeId), `Unknown avatar route ${routeId} for ${policy.persona}`);
        }
        for (const routeId of policy.navigation.structurallyForbiddenRouteIds) {
            assert(knownRoutes.has(routeId), `Unknown structurally forbidden route ${routeId} for ${policy.persona}`);
        }
        for (const routeId of policy.navigation.conditionallyAvailableRouteIds) {
            assert(knownRoutes.has(routeId), `Unknown conditionally available route ${routeId} for ${policy.persona}`);
        }
        for (const bundleId of policy.defaultPermissionBundleIds) {
            assert(Boolean(PERMISSION_BUNDLE_REGISTRY[bundleId]), `Unknown permission bundle ${bundleId}`);
        }
        for (const moduleId of policy.baselineModuleIds) {
            assert(MODULE_KEYS.includes(moduleId), `Unknown module ID ${moduleId}`);
        }
    }
    const orgClientPolicy = PERSONA_POLICY_REGISTRY.ORG_CLIENT;
    assert(!orgClientPolicy.administration.managesOrganizations &&
        !orgClientPolicy.administration.managesPlatformModules &&
        !orgClientPolicy.administration.managesPlatformUsers &&
        !orgClientPolicy.administration.managesBranches &&
        !orgClientPolicy.administration.managesOrganizationModules &&
        !orgClientPolicy.administration.managesOrganizationUsers &&
        !orgClientPolicy.administration.managesRoles &&
        !orgClientPolicy.administration.managesClients, 'ORG_CLIENT must not manage administrative resources');
    assert(PERSONA_POLICY_REGISTRY.PLATFORM_ROOT.dataVisibility.organizationBusinessData === 'DELEGATED_ONLY', 'PLATFORM_ROOT must have delegated-only organization data visibility');
    assert(PERSONA_POLICY_REGISTRY.PLATFORM_ADMIN.dataVisibility.organizationBusinessData === 'DELEGATED_ONLY', 'PLATFORM_ADMIN must have delegated-only organization data visibility');
    const orgAdmin = PERSONA_POLICY_REGISTRY.ORG_ADMIN;
    assert(!orgAdmin.administration.managesOrganizations, 'ORG_ADMIN cannot manage platform organizations');
    assert(!orgAdmin.administration.managesPlatformModules, 'ORG_ADMIN cannot manage platform modules');
    assert(!PERSONA_POLICY_REGISTRY.PLATFORM_ROOT.administration.managesBranches, 'PLATFORM_ROOT cannot directly manage branches');
    assert(!PERSONA_POLICY_REGISTRY.PLATFORM_ADMIN.administration.managesBranches, 'PLATFORM_ADMIN cannot directly manage branches');
    assert(!PERSONA_POLICY_REGISTRY.PLATFORM_ROOT.administration.managesClients, 'PLATFORM_ROOT cannot directly manage clients');
    assert(!PERSONA_POLICY_REGISTRY.PLATFORM_ADMIN.administration.managesClients, 'PLATFORM_ADMIN cannot directly manage clients');
    assert(PERSONA_POLICY_REGISTRY.ORG_ADMIN.administration.managesBranches, 'Only organization personas may directly manage branches');
    assert(PERSONA_POLICY_REGISTRY.ORG_ADMIN.administration.managesClients, 'Only organization personas may directly manage clients');
    assert(PERSONA_POLICY_REGISTRY.PLATFORM_ROOT.administration.managesOrganizations, 'Platform Root must manage platform organizations');
    assert(PERSONA_POLICY_REGISTRY.PLATFORM_ROOT.administration.managesPlatformModules, 'Platform Root must manage platform modules');
    assert(PERSONA_POLICY_REGISTRY.ORG_ADMIN.dataVisibility.organizationBusinessData === 'ACTIVE_ORGANIZATION', 'Org Admin must have ACTIVE_ORGANIZATION data visibility');
    assert(PERSONA_POLICY_REGISTRY.ORG_CLIENT.mutationPolicy === 'READ_ONLY', 'Org Client must be READ_ONLY by default');
}
validatePersonaPolicyRegistry();
